import { IPlayImageHotspotTask } from 'common/modules/ReactShared';
import React, { useCallback, useEffect, useState } from 'react';
import DashedPagination from 'students/views/shared/components/DashedPagination';
import ImageHotspotItem from './ImageHotspotItem';
import { ISelectAnswerPayload } from './types';

interface IProps {
  task: IPlayImageHotspotTask;
  onSelectAnswer: (payload: ISelectAnswerPayload) => void;
  onComplete: () => void;
  onSkip: (itemId: number) => void;
}

const PlayImageHotspotMainScreen: React.FC<IProps> = ({
  task,
  onSelectAnswer,
  onComplete,
  onSkip
}) => {
  const { items, imageURL, image: imageSize } = task;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [uncompleted, setUncompleted] = useState<number[]>([]);
  const completedCount = items.length - uncompleted.length;

  useEffect(() => {
    setUncompleted(items.map((_, index) => index));
  }, [items]);

  const handleComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    const newUncompleted = uncompleted.filter((index) => index !== currentIndex);
    const newIndex = newUncompleted[0];
    if (newIndex === undefined) {
      setIsCompleted(true);
      setTimeout(() => handleComplete(), 1000);
    } else {
      setCurrentIndex(newIndex);
      setUncompleted(newUncompleted);
    }
  }, [handleComplete, currentIndex, uncompleted]);

  const handleSelectAnswer = useCallback(
    (payload: ISelectAnswerPayload) => {
      onSelectAnswer(payload);
      handleNext();
    },
    [handleNext, onSelectAnswer]
  );

  const handleSelectItem = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleItemNext = () => {
    onSkip(items[currentIndex].id);

    handleNext();
  };

  const handleItemPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((currentIndex) => currentIndex - 1);
      setUncompleted([currentIndex - 1, ...uncompleted]);
    }
  };

  return (
    <>
      <ImageHotspotItem
        isCompleted={isCompleted}
        currentIndex={currentIndex}
        items={items}
        onSelectAnswer={handleSelectAnswer}
        onItemSelect={handleSelectItem}
        image={{ url: imageURL, size: imageSize }}
      />

      <DashedPagination
        itemsNumber={items.length}
        currentIndex={completedCount}
        taskId={task.id}
        onNextItem={handleItemNext}
        onPrevItem={handleItemPrev}
      />
    </>
  );
};

export default PlayImageHotspotMainScreen;
